import { useEffect } from 'react';
import type { LoudnessTarget, StereoWidthPreset, TrackState } from '@melodinai/types';
import { useMixerStore } from '../../stores/mixerStore';
import { SslMeter } from './meters/SslMeter';
import { clsx } from '../../lib/utils';

const WIDTHS: { id: StereoWidthPreset; label: string }[] = [
  { id: 'mono', label: 'Mono' },
  { id: 'narrow', label: 'Narrow' },
  { id: 'wide', label: 'Wide' },
  { id: 'ultraWide', label: 'Ultra Wide' },
];

const TARGETS: { id: LoudnessTarget; label: string }[] = [
  { id: 'streaming', label: 'Streaming' },
  { id: 'club', label: 'Club' },
  { id: 'radio', label: 'Radio' },
  { id: 'festival', label: 'Festival' },
];

interface MasterBusPanelProps {
  master: TrackState | null;
  compact?: boolean;
}

function approxLufs(rms: number): number {
  if (rms <= 0.0001) return -70;
  return Math.max(-70, 20 * Math.log10(rms) - 0.691);
}

export function MasterBusPanel({ master, compact }: MasterBusPanelProps) {
  const mode = useMixerStore((s) => s.mode);
  const meters = useMixerStore((s) => s.meters);
  const ensureMasterBus = useMixerStore((s) => s.ensureMasterBus);
  const setFxParam = useMixerStore((s) => s.setFxParam);
  const applyCompPreset = useMixerStore((s) => s.applyCompPreset);
  const widthPreset = useMixerStore((s) => s.widthPreset);
  const setWidthPreset = useMixerStore((s) => s.setWidthPreset);
  const loudnessTarget = useMixerStore((s) => s.loudnessTarget);
  const setLoudnessTarget = useMixerStore((s) => s.setLoudnessTarget);

  useEffect(() => {
    ensureMasterBus();
  }, [ensureMasterBus]);

  if (!master) {
    return (
      <div className="border-t border-ssl-metal/40 px-3 py-2 text-[10px] text-ssl-muted">
        Master bus unavailable
      </div>
    );
  }

  const m = meters[master.id] || meters.__master;
  const peak = Math.max(m?.peakL || 0, m?.peakR || 0);
  const rms = ((m?.rmsL || 0) + (m?.rmsR || 0)) / 2;
  const short = approxLufs(rms);
  const integrated = approxLufs(rms * 0.85);
  const dr = Math.max(0, short - approxLufs(peak || 0.001));
  const lim = master.fxChain?.find((f) => f.type === 'Limiter');
  const ceiling = Number((lim?.params as Record<string, number> | undefined)?.ceiling ?? -1);
  const comp = master.fxChain?.find((f) => f.type === 'Compressor');
  const cp = (comp?.params || {}) as Record<string, number>;

  return (
    <div className={clsx('border-t border-ssl-metal/50 bg-ssl-graphite', compact ? 'px-2 py-1.5' : 'px-3 py-2')}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ssl-accent">Master</h3>
        <button
          type="button"
          className="rounded border border-ssl-metal/50 px-1.5 py-0.5 text-[9px] text-ssl-muted hover:text-ssl-accent"
          onClick={() => applyCompPreset(master.id, 'masterBusGlue')}
        >
          Glue
        </button>
      </div>

      <div className="flex gap-3">
        <SslMeter
          peakL={m?.peakL}
          peakR={m?.peakR}
          rmsL={m?.rmsL}
          rmsR={m?.rmsR}
          grDb={m?.grDb}
          height={compact ? 56 : 72}
          showGr
        />
        <div className="min-w-0 flex-1 space-y-2">
          {mode === 'studio' && (
            <div className="grid grid-cols-2 gap-1">
              <label className="text-[8px] text-ssl-muted">
                Thresh
                <input
                  type="range"
                  min={-40}
                  max={0}
                  step={0.5}
                  value={cp.threshold ?? -12}
                  onChange={(e) => setFxParam(master.id, 'Compressor', 'threshold', Number(e.target.value))}
                  className="mt-0.5 h-1 w-full accent-ssl-led"
                />
              </label>
              <label className="text-[8px] text-ssl-muted">
                Ratio
                <input
                  type="range"
                  min={1}
                  max={10}
                  step={0.1}
                  value={cp.ratio ?? 2}
                  onChange={(e) => setFxParam(master.id, 'Compressor', 'ratio', Number(e.target.value))}
                  className="mt-0.5 h-1 w-full accent-ssl-led"
                />
              </label>
            </div>
          )}

          <div className="flex flex-wrap gap-1">
            {WIDTHS.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => setWidthPreset(w.id)}
                className={clsx(
                  'rounded px-1.5 py-0.5 text-[8px]',
                  widthPreset === w.id
                    ? 'bg-ssl-accent/20 text-ssl-accent'
                    : 'bg-ssl-rail text-ssl-muted',
                )}
              >
                {w.label}
              </button>
            ))}
          </div>

          {mode === 'simple' ? (
            <label className="flex items-center gap-2 text-[9px] text-ssl-muted">
              <span>Loudness</span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={loudnessTarget === 'festival' ? 0.92 : loudnessTarget === 'club' ? 0.78 : loudnessTarget === 'radio' ? 0.7 : 0.55}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (v > 0.85) setLoudnessTarget('festival');
                  else if (v > 0.72) setLoudnessTarget('club');
                  else if (v > 0.6) setLoudnessTarget('radio');
                  else setLoudnessTarget('streaming');
                }}
                className="h-1 flex-1 accent-ssl-accent"
              />
            </label>
          ) : (
            <label className="flex items-center gap-2 text-[9px] text-ssl-muted">
              <span className="w-12">Ceiling</span>
              <input
                type="range"
                min={-6}
                max={0}
                step={0.1}
                value={ceiling}
                onChange={(e) => setFxParam(master.id, 'Limiter', 'ceiling', Number(e.target.value))}
                className="h-1 flex-1 accent-ssl-ledClip"
              />
              <span className="tabular-nums">{ceiling.toFixed(1)}</span>
            </label>
          )}

          <div className="flex flex-wrap gap-1">
            {TARGETS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setLoudnessTarget(t.id)}
                className={clsx(
                  'rounded px-1.5 py-0.5 text-[8px]',
                  loudnessTarget === t.id
                    ? 'bg-ssl-led/20 text-ssl-led'
                    : 'bg-ssl-rail text-ssl-muted',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-4 gap-1 text-[8px] tabular-nums text-ssl-muted">
            <div>
              <div className="text-ssl-text">{integrated.toFixed(1)}</div>
              LUFS Int
            </div>
            <div>
              <div className="text-ssl-text">{short.toFixed(1)}</div>
              Short
            </div>
            <div>
              <div className="text-ssl-text">{(20 * Math.log10(Math.max(0.0001, peak))).toFixed(1)}</div>
              Peak
            </div>
            <div>
              <div className="text-ssl-text">{dr.toFixed(1)}</div>
              DR
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
